import { createSuccessResponse, createErrorResponse, AppError } from '../utils/errorHandler';

class AdminAuthService {
  constructor() {
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    this.tokenKey = 'adminToken';
    this.userKey = 'adminUser';
  }

  /**
   * Authenticate admin user
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise<Object>} Authentication result
   */
  async login(email, password) {
    try {
      const response = await fetch(`${this.backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new AppError(result.error || 'Login failed', response.status);
      }

      // Store token and user info securely
      localStorage.setItem(this.tokenKey, result.token);
      localStorage.setItem(this.userKey, result.user.email);
      
      return createSuccessResponse(result.user, 'Login successful');
    } catch (error) {
      return createErrorResponse(error);
    }
  }

  /**
   * Logout admin user
   */
  logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = localStorage.getItem(this.tokenKey);
    const user = localStorage.getItem(this.userKey);
    return !!(token && user);
  }

  /**
   * Get current admin user
   * @returns {string|null} Admin email or null
   */
  getCurrentUser() {
    return localStorage.getItem(this.userKey);
  }

  /**
   * Get authentication token
   * @returns {string|null} Token or null
   */
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Verify token with backend
   * @returns {Promise<Object>} Verification result
   */
  async verifyToken() {
    try {
      const token = this.getToken();
      if (!token) {
        throw new AppError('No token found', 401);
      }

      const response = await fetch(`${this.backendUrl}/api/admin/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();
      
      if (!result.success) {
        this.logout(); // Clear invalid token
        throw new AppError(result.error || 'Token verification failed', response.status);
      }

      return createSuccessResponse(result.user, 'Token valid');
    } catch (error) {
      this.logout();
      return createErrorResponse(error);
    }
  }

  /**
   * Get authorization headers for API calls
   * @returns {Object} Headers object
   */
  getAuthHeaders() {
    const token = this.getToken();
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  }
}

export default new AdminAuthService();